import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useStyles } from '~/assets/styles';
import Menu from '~/components/core/Atomic/Menu';
import { FsMenuItem } from '~/components/core/FsWrappers';

import WithConfirm from '../ConfirmModal';
import { ThreeDotsIcon } from '../icons';
import InlineIconButton from '../InlineIconButton';

const ThreeDotsMenu = ({
  options,
  onClick,
  ignorePermission,
  stopPropagation,
  disabled,
  anchorRef,
  disableScrollLock,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { classes } = useStyles();

  return (
    <>
      <InlineIconButton
        icon={ThreeDotsIcon}
        ignorePermissions={ignorePermission}
        className={`${classes.textIcon} ${classes.marginedIcon} ${classes.hoverableNonFilledIcon}`}
        onClick={(event) => {
          if (stopPropagation) {
            event.stopPropagation();
          }
          if (onClick) {
            onClick();
          }
          setAnchorEl(anchorRef?.current || event.currentTarget);
        }}
        disabled={disabled}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        disableScrollLock={disableScrollLock}
      >
        {options.map(
          ({ onClick, onOpenConfirmModal, disabled, node, key, withConfirmProps, ignorePermissions, tooltipText }) => {
            const closeMenu = () => setAnchorEl(null);

            const withConfirmPropsWithCloseMenu = {
              ...(withConfirmProps || {}),
              postOnClick: (...args) => {
                withConfirmProps.postOnClick && withConfirmProps.postOnClick(...args);
                closeMenu();
              },
              onSecondaryBtnClick: (...args) => {
                withConfirmProps.onSecondaryBtnClick && withConfirmProps.onSecondaryBtnClick(...args);
                closeMenu();
              },
            };

            const MenuItemComponent = (
              <FsMenuItem
                key={key}
                onClick={async (e) => {
                  if (withConfirmProps) {
                    return;
                  }
                  await onClick(e);
                  closeMenu();
                }}
                disabled={disabled}
                ignorePermissions={ignorePermissions}
                tooltipText={tooltipText}
              >
                {node}
              </FsMenuItem>
            );

            return withConfirmProps ? (
              <WithConfirm
                {...withConfirmPropsWithCloseMenu}
                key={key}
                onOpenConfirmModal={withConfirmPropsWithCloseMenu.onOpenConfirmModal ?? onOpenConfirmModal}
              >
                <div
                  onClick={async (e) => {
                    await onClick(e);
                    closeMenu();
                  }}
                >
                  {MenuItemComponent}
                </div>
              </WithConfirm>
            ) : (
              MenuItemComponent
            );
          }
        )}
      </Menu>
    </>
  );
};

ThreeDotsMenu.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      onClick: PropTypes.func,
      onOpenConfirmModal: PropTypes.func,
      disabled: PropTypes.bool,
      node: PropTypes.node.isRequired,
      key: PropTypes.string.isRequired,
      withConfirmProps: PropTypes.object,
      ignorePermissions: PropTypes.bool,
      tooltipText: PropTypes.string,
    })
  ).isRequired,
  onClick: PropTypes.func,
  ignorePermission: PropTypes.bool,
  stopPropagation: PropTypes.bool,
  disabled: PropTypes.bool,
  anchorRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  disableScrollLock: PropTypes.bool,
};

export default ThreeDotsMenu;
